// Who won a tender: SIMAP's award notice names the vendors, where they are
// and what they offered. Read in the details panel, on the row marker and in
// the export.

export interface AwardVendor {
    name?: string | null;
    city?: string | null;
    canton?: string | null;
    country?: string | null;
    price?: number | null;
    currency?: string | null;   // 'chf', 'eur', …
    vat?: string | null;        // SIMAP vatType: 'full' = incl. VAT, 'no_vat' = excl. VAT
    rank?: number | null;
    note?: string | null;
}

const LIST_SEPARATOR = '; ';

// Whole francs with Swiss thousands separators: 1'422'938.
const swissNumber = (n: number): string =>
    Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

const VAT_WORDING: Record<string, string> = { full: 'incl. VAT', no_vat: 'excl. VAT' };

const vendorName = (vendor: AwardVendor): string => vendor.name?.trim() || 'Unnamed vendor';

// "CHF 1'422'938 incl. VAT"; empty without a price.
export function formatAwardPrice(vendor: AwardVendor): string {
    if (vendor.price == null || isNaN(vendor.price)) return '';
    const currency = (vendor.currency || 'chf').toUpperCase();
    const vat = vendor.vat ? VAT_WORDING[vendor.vat] : undefined;
    return [`${currency} ${swissNumber(vendor.price)}`, vat].filter(Boolean).join(' ');
}

// "Acme AG, Bern (BE) · CHF 1'422'938 incl. VAT"
export function vendorLine(vendor: AwardVendor): string {
    const where = [vendor.city, vendor.canton ? `(${vendor.canton})` : null].filter(Boolean).join(' ');
    const who = where ? `${vendorName(vendor)}, ${where}` : vendorName(vendor);
    const price = formatAwardPrice(vendor);
    return price ? `${who} · ${price}` : who;
}

const FULL_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

// A date-only string ("2026-05-04") as a local date, so the day never shifts
// with the time zone.
const localDate = (value: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

// "5 offers · awarded 4 May 2026"
export function awardMeta(submissions: number | null | undefined, awardDate: string | null | undefined): string {
    const parts: string[] = [];
    if (submissions != null) parts.push(`${submissions} ${submissions === 1 ? 'offer' : 'offers'}`);
    const date = awardDate ? localDate(awardDate) : null;
    if (date) parts.push(`awarded ${date.toLocaleDateString('en-GB', FULL_DATE)}`);
    return parts.join(' · ');
}

// Export cells: the names, and the prices as published (numbers, not text).
export function winnerNames(vendors: AwardVendor[] | null | undefined): string {
    return (vendors ?? []).map(vendorName).join(LIST_SEPARATOR);
}

export function winnerPrices(vendors: AwardVendor[] | null | undefined): string {
    return (vendors ?? []).filter(v => v.price != null).map(v => String(v.price)).join(LIST_SEPARATOR);
}
